import React, { useEffect, useId, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DropdownProps } from 'semantic-ui-react';
import get from 'lodash.get';
import Form from '../../form';
import { useAppDispatch } from '../../../hooks';
import {
  getCampaignOption,
  getEntityOption
} from '../../../helpers/entityOptionHelper';
import { reset, searchEntities } from '../../../modules/autocomplete';

interface ICampaignLookupProps extends DropdownProps {
  label?: string;
  name?: string;
  value?: number;
  validation?: any;
  customFilter?: any[];
  customDisplayName?: (data: any) => string;
}

const campaignRequest = (text: string, customFilter: any[] = []) => ({
  page: 1,
  pageSize: 75,
  resultType: 'Full',
  text,
  orderBy: {
    key: 'name',
    direction: 'asc'
  },
  filters: [
    {
      key: 'status',
      operator: 'EqualTo',
      value: 1
    },
    ...customFilter
  ]
});

export const CampaignLookup = ({
  customFilter,
  customDisplayName,
  ...props
}: ICampaignLookupProps) => {
  const dispatch = useAppDispatch();
  const generatedId = useId();
  // Results are kept per field in the autocomplete store.
  const path = `campaignLookup.${String(props.id ?? generatedId).replace(/[^\w-]/g, '_')}`;
  const loading = useSelector((state: any) =>
    get(state, `autocomplete.${path}.loading`)
  );
  const list: any[] =
    useSelector((state: any) => get(state, `autocomplete.${path}.data.list`)) ??
    [];
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const searchCampaigns = (text: string) =>
    dispatch(
      searchEntities('page/campaign', path, campaignRequest(text, customFilter))
    );

  const options = list.map((x) =>
    customDisplayName
      ? getEntityOption(x, { text: customDisplayName(x) })
      : getCampaignOption(x)
  );

  const onCampaignSearch = (_: any, searchTerm: any) => {
    timer.current && clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      searchCampaigns(searchTerm.searchQuery);
    }, 500);
  };

  useEffect(() => {
    searchCampaigns('');
    return () => {
      timer.current && clearTimeout(timer.current);
      dispatch(reset(path));
    };
  }, []);

  return (
    <Form.Autocomplete
      {...props}
      search={onCampaignSearch}
      options={options}
      loading={!!loading}
      clear={() => searchCampaigns('')}
    />
  );
};
